import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpHeaders,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, BehaviorSubject, from, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { CognitoService } from 'src/app/auth/services/cognito.service';
import { switchMap, finalize, catchError } from 'rxjs/operators';

export interface ApiErrorResponse {
  statusCode: number;
  timestamp: string;
  path: string;
  message: string;
  error?: string;
  errors?: any[];  // Errores de Shinkansen
  details?: any;
}

export interface ShinkansenErrorDetails {
  field?: string;
  code?: string;
  description?: string;
}

export interface ShinkansenPayoutRequest {
  amount: number;
  currency: string;
  creditorName: string;
  creditorRut: string;
  creditorBank: string;
  creditorAccountNumber: string;
  creditorAccountType: string;
  creditorEmail?: string;
  description?: string;
  reference?: string;
  debtorAccountNumber?: string;
  walletTransactionId?: string; // ID de la transacción de retiro (se guarda en reference)
  usuarioId?: number; // ID del usuario
}

export interface ShinkansenPayoutResponse {
  success: boolean;
  payout: {
    id: string;
    transactionId: string;
    status: string;
    amount: number;
    currency: string;
  };
  message: string;
}

@Injectable({
  providedIn: 'root',
})
export class ShinkansenPayoutService {
  private apiUrl = environment.apiNest;
  private loadingSubject = new BehaviorSubject<boolean>(false);
  public loading$ = this.loadingSubject.asObservable();

  constructor(
    private http: HttpClient,
    private cognitoService: CognitoService,
  ) {}

  // Método para obtener los headers con el token
  private async getHeaders(): Promise<HttpHeaders> {
    const session = await this.cognitoService.getSession();
    if (!session || !session.accessToken || !session.accessToken.jwtToken) {
      throw new Error('No se pudo obtener el token de sesión');
    }

    const token = session.accessToken.jwtToken.trim();
    return new HttpHeaders().set('Authorization', `Bearer ${token}`);
  }

  private handleError(error: HttpErrorResponse) {

    let errorMessage = 'Ha ocurrido un error en el servicio';
    let errorCode = 'UNKNOWN_ERROR';
    let errorDetails: any = null;

    if (error.error instanceof ErrorEvent) {
      // Error del lado del cliente (red, etc.)
      errorMessage = `Error de conexión: ${error.error.message}`;
      errorCode = 'CLIENT_ERROR';
    } else {
      // Error del lado del servidor
      const apiError = error.error as ApiErrorResponse;


      // Intentar obtener errores de Shinkansen
      let shinkansenErrors: any[] = [];

      // Primero verificar si vienen directamente en apiError.errors (nuevo formato del backend)
      if (apiError?.errors && Array.isArray(apiError.errors)) {
        shinkansenErrors = apiError.errors;
      }
      // Si no, intentar parsear desde el mensaje (formato antiguo)
      else if (apiError?.message) {
        try {
          // El mensaje puede venir como: "Invalid payout data: {...}"
          const messageStr = apiError.message;

          // Buscar JSON dentro del mensaje
          const jsonMatch = messageStr.match(/\{.*\}/s);
          if (jsonMatch) {
            const parsedData = JSON.parse(jsonMatch[0]);
            if (parsedData.errors && Array.isArray(parsedData.errors)) {
              shinkansenErrors = parsedData.errors;
            }
          }
        } catch (parseError) {
        }
      }

      // Capturar detalles adicionales del error
      errorDetails = shinkansenErrors.length > 0 ? shinkansenErrors : (apiError?.details || null);

      switch (error.status) {
        case 400:
          errorCode = 'BAD_REQUEST';

          // Si hay errores parseados de Shinkansen, usarlos
          if (shinkansenErrors.length > 0) {
            const firstError = shinkansenErrors[0];
            const errorCodeShinkansen = firstError.error_code || '';
            const errorMessageShinkansen = firstError.error_message || '';


            // Mapear errores específicos de Shinkansen a mensajes claros
            if (errorCodeShinkansen === 'invalid_rut') {
              errorCode = 'INVALID_RUT';
              errorMessage = `RUT inválido: ${errorMessageShinkansen}. Verifique que el RUT sea correcto y tenga el dígito verificador válido.`;
            } else if (errorCodeShinkansen === 'invalid_bank') {
              errorCode = 'INVALID_BANK';
              errorMessage = 'Banco no válido o no soportado por Shinkansen. Verifique el nombre del banco.';
            } else if (errorCodeShinkansen === 'invalid_account') {
              errorCode = 'INVALID_ACCOUNT';
              errorMessage = 'Número de cuenta inválido. Verifique que el número de cuenta sea correcto.';
            } else if (errorCodeShinkansen === 'invalid_amount') {
              errorCode = 'INVALID_AMOUNT';
              errorMessage = 'Monto inválido. Verifique el monto a transferir.';
            } else if (errorCodeShinkansen === 'insufficient_funds') {
              errorCode = 'INSUFFICIENT_FUNDS';
              errorMessage = 'Fondos insuficientes para realizar la transferencia.';
            } else if (errorCodeShinkansen === 'invalid_schema_value') {
              errorCode = 'INVALID_SCHEMA_VALUE';
              errorMessage = `Error de validación de esquema: ${errorMessageShinkansen || 'El valor proporcionado no es válido en el esquema de Shinkansen.'}`;
            } else {
              // Error genérico de Shinkansen - usar el código de error de Shinkansen
              errorCode = errorCodeShinkansen ? errorCodeShinkansen.toUpperCase() : 'BAD_REQUEST';
              errorMessage = errorMessageShinkansen || apiError?.message || 'Error de validación en los datos del payout.';
            }
          } else {
            // Fallback a detección por palabra clave en el mensaje
            if (apiError?.message?.includes('RUT')) {
              errorCode = 'INVALID_RUT';
              errorMessage = 'RUT inválido. Verifique que el formato sea correcto (ej: 12345678-9).';
            } else if (apiError?.message?.includes('bank') || apiError?.message?.includes('banco')) {
              errorCode = 'INVALID_BANK';
              errorMessage = 'Banco no válido o no soportado por Shinkansen.';
            } else if (apiError?.message?.includes('account')) {
              errorCode = 'INVALID_ACCOUNT';
              errorMessage = 'Número de cuenta inválido. Verifique los datos de la cuenta.';
            } else if (apiError?.message?.includes('amount') || apiError?.message?.includes('monto')) {
              errorCode = 'INVALID_AMOUNT';
              errorMessage = 'Monto inválido. Verifique el monto a transferir.';
            } else if (apiError?.message?.includes('insufficient')) {
              errorCode = 'INSUFFICIENT_FUNDS';
              errorMessage = 'Fondos insuficientes para realizar la transferencia.';
            } else {
              errorMessage = apiError?.message || 'Los datos proporcionados no son válidos. Verifique la información e intente nuevamente.';
            }
          }
          break;

        case 401:
          // El backend responde 401 por tres motivos distintos: token ausente,
          // token vencido y cuenta fuera del grupo `aguacate_operator`. Decir
          // "sesión expirada" a secas mandaba al operador a reloguearse una y
          // otra vez cuando el problema era de permisos.
          errorCode = 'UNAUTHORIZED';
          errorMessage =
            'No pudimos validar tu sesión o tu cuenta no tiene permiso de operador. ' +
            'Cierra sesión y vuelve a entrar; si sigue igual, avisa a soporte.';
          break;

        case 403:
          errorCode = 'FORBIDDEN';
          errorMessage = 'No tiene permisos para realizar esta operación con Shinkansen.';
          break;

        case 404:
          errorCode = 'NOT_FOUND';
          errorMessage = 'El servicio de Shinkansen no está disponible. Intente más tarde.';
          break;

        case 422:
          errorCode = 'VALIDATION_ERROR';
          errorMessage = apiError?.message || 'Error de validación. Verifique que todos los datos sean correctos.';
          break;

        case 500:
          errorCode = 'SERVER_ERROR';
          errorMessage = 'Error interno del servidor de Shinkansen. Por favor, contacte al administrador.';
          break;

        case 502:
        case 503:
        case 504:
          errorCode = 'SERVICE_UNAVAILABLE';
          errorMessage = 'El servicio de Shinkansen no está disponible temporalmente. Intente más tarde.';
          break;

        default:
          errorCode = 'UNKNOWN_ERROR';
          errorMessage = apiError?.message || `Error desconocido (código ${error.status}). Contacte al administrador.`;
      }
    }

    // Log completo del error para debugging

    return throwError(() => ({
      message: errorMessage,
      code: errorCode,
      details: errorDetails,
      originalError: error
    }));
  }

  /**
   * Envía un pago a través de Shinkansen
   * @param payoutData - Datos del pago a enviar
   * @returns Observable con la respuesta del pago
   */
  createPayout(
    payoutData: ShinkansenPayoutRequest,
  ): Observable<ShinkansenPayoutResponse> {

    // Activar el loading
    this.loadingSubject.next(true);

    return from(this.getHeaders()).pipe(
      switchMap((headers) => {
        return this.http.post<ShinkansenPayoutResponse>(
          `${this.apiUrl}shinkansen/payouts`,
          payoutData,
          { headers },
        );
      }),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
      finalize(() => {
        // Desactivar el loading cuando finalice (ya sea éxito o error)
        this.loadingSubject.next(false);
      }),
    );
  }

  /**
   * Lista los payouts en `manual_pending`: envíos automáticos que fallaron o no
   * aplicaron y quedaron para pago manual. Estas transacciones NO aparecen en la
   * lista de "retiro FIAT pendientes" (esa carga POR_PROCESAR; estas ya están
   * ENVIADA), por eso se listan aparte. Cada item trae el motivo (autoAttemptOrigin
   * + errorMessage) y `reference` (id de la tx) para pagar con el flujo manual.
   */
  getManualPending(
    limit: number = 50,
    offset: number = 0,
  ): Observable<{ items: any[]; total: number }> {
    return from(this.getHeaders()).pipe(
      switchMap((headers) =>
        this.http.get<{ items: any[]; total: number }>(
          `${this.apiUrl}shinkansen/payouts/manual-pending?limit=${limit}&offset=${offset}`,
          { headers },
        ),
      ),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  /**
   * Cierra un payout que quedó en `manual_pending` declarando qué pasó con la
   * plata: `manual_paid` (se transfirió por fuera de Shinkansen) o `cancelled`
   * (no se va a pagar). Sin este cierre la fila queda abierta para siempre y la
   * pantalla sigue ofreciendo "Pagar" sobre un retiro ya resuelto.
   *
   * Un cierre `manual_paid` cuenta como pago hecho: el backend bloquea cualquier
   * payout posterior para esa transacción.
   *
   * @param reference - id de la transacción wallet
   */
  resolveManualPending(
    reference: string,
    body: {
      outcome: 'manual_paid' | 'cancelled';
      comprobante?: string;
      banco?: string;
      nota?: string;
    },
  ): Observable<{ success: boolean; payout: any; message: string }> {
    return from(this.getHeaders()).pipe(
      switchMap((headers) =>
        this.http.post<{ success: boolean; payout: any; message: string }>(
          `${this.apiUrl}shinkansen/payouts/manual-pending/${reference}/resolve`,
          body,
          { headers },
        ),
      ),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  /**
   * Obtiene el estado de un payout por ID de transacción
   * @param transactionId - ID de la transacción a consultar
   * @returns Observable con el estado del pago
   */
  getPayoutStatus(transactionId: string): Observable<any> {

    return from(this.getHeaders()).pipe(
      switchMap((headers) => {
        const url = `${this.apiUrl}shinkansen/payouts/transactions/${transactionId}/status-by-id`;
        return this.http.get(url, { headers });
      }),
      catchError((error: HttpErrorResponse) => {
        return this.handleError(error);
      }),
    );
  }

  /**
   * Obtiene un payout específico por ID de transacción
   * @param transactionId - ID de la transacción
   * @returns Observable con los detalles del payout
   */
  getPayoutByTransactionId(transactionId: string): Observable<any> {

    return from(this.getHeaders()).pipe(
      switchMap((headers) => {
        const url = `${this.apiUrl}shinkansen/payouts/${transactionId}`;
        return this.http.get(url, { headers });
      }),
      catchError((error: HttpErrorResponse) => {
        return this.handleError(error);
      }),
    );
  }

  getMontoMaxAuto(): Observable<{ success: boolean; valor: number | null }> {
    return from(this.getHeaders()).pipe(
      switchMap((headers) =>
        this.http.get<{ success: boolean; valor: number | null }>(
          `${this.apiUrl}shinkansen/payouts/monto-max-auto`,
          { headers },
        ),
      ),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  getPagoAutomatico(): Observable<{ success: boolean; activo: boolean }> {
    return from(this.getHeaders()).pipe(
      switchMap((headers) =>
        this.http.get<{ success: boolean; activo: boolean }>(
          `${this.apiUrl}shinkansen/payouts/pago-automatico`,
          { headers },
        ),
      ),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  setPagoAutomatico(activo: boolean): Observable<{ success: boolean; activo: boolean }> {
    return from(this.getHeaders()).pipe(
      switchMap((headers) =>
        this.http.post<{ success: boolean; activo: boolean }>(
          `${this.apiUrl}shinkansen/payouts/pago-automatico`,
          { activo },
          { headers },
        ),
      ),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  updateMontoMaxAuto(valor: number): Observable<{ success: boolean; valor: number }> {
    return from(this.getHeaders()).pipe(
      switchMap((headers) =>
        this.http.post<{ success: boolean; valor: number }>(
          `${this.apiUrl}shinkansen/payouts/monto-max-auto`,
          { valor },
          { headers },
        ),
      ),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  /**
   * Obtiene el estado de payout por wallet transaction ID
   * @param walletTransactionId - ID de la transacción del wallet
   * @returns Observable con el estado del payout
   */
  getPayoutStatusByWalletTransactionId(walletTransactionId: string): Observable<{
    exists: boolean;
    status?: string;
    payoutId?: string;
    createdAt?: Date;
    completedAt?: Date;
    canRetry?: boolean;
    errorCode?: string;
    errorMessage?: string;
    friendlyMessage?: string;
    shinkansenTransactionId?: string;
  }> {

    return from(this.getHeaders()).pipe(
      switchMap((headers) => {
        const url = `${this.apiUrl}shinkansen/payouts/wallet-transaction/${walletTransactionId}/status`;
        return this.http.get<{
          exists: boolean;
          status?: string;
          payoutId?: string;
          createdAt?: Date;
          completedAt?: Date;
          canRetry?: boolean;
          errorCode?: string;
          errorMessage?: string;
          friendlyMessage?: string;
          shinkansenTransactionId?: string;
        }>(url, { headers });
      }),
      catchError((error: HttpErrorResponse) => {
        // Si hay error, asumir que no existe
        return throwError(() => error);
      }),
    );
  }
}
